import { useEffect, useState } from 'react';
import type { ChatContext, CtaErrorDetail } from '@chat-emp/shared';
import { onBridgeMessage } from '../lib/bridge.js';
import { reportCtaError } from '../lib/cta.js';
import { setHostViewport } from '../lib/viewport.js';

/**
 * Handles the host → iframe "props" messages the bridge defines:
 *   - `set-theme`   → applies the theme to <html data-theme> (CSS / stream-chat-react pick it up)
 *   - `set-context` → tracks the current job/candidate context for the UI to consume
 *
 * Origin validation is bridge.ts's job; this only interprets accepted messages.
 */
export function useHostProps(): { context: ChatContext; theme: string | null } {
  const [context, setContext] = useState<ChatContext>({});
  const [theme, setTheme] = useState<string | null>(null);

  useEffect(() => {
    return onBridgeMessage((env) => {
      if (env.type === 'set-context') {
        setContext((env.payload as ChatContext | undefined) ?? {});
      } else if (env.type === 'set-theme') {
        const next = (env.payload as { theme?: string } | undefined)?.theme;
        if (next) setTheme(next);
      } else if (env.type === 'set-viewport') {
        // The host's viewport, not the iframe's — see lib/viewport.ts.
        const mobile = (env.payload as { mobile?: boolean } | undefined)?.mobile;
        if (typeof mobile === 'boolean') setHostViewport(mobile);
      } else if (env.type === 'message-cta-click-error') {
        // An `event`-mode CTA the host took on has failed. Relayed to the one
        // button still spinning for it — see lib/cta.ts.
        const detail = env.payload as CtaErrorDetail | undefined;
        if (detail?.messageId) reportCtaError(detail);
      }
    });
  }, []);

  useEffect(() => {
    if (theme) document.documentElement.dataset.theme = theme;
  }, [theme]);

  return { context, theme };
}
